import ErrorMessage from '@app/components/atoms/ErrorMessage/ErrorMessage'
import InputUpload from '@app/components/atoms/Images/InputUpload'
import LabelField from '@app/components/atoms/LabelField/LabelField'
import Section from '@app/components/atoms/Section/Section'
import { toTextFieldDateFormat } from '@app/libs/format'
import BasicInformationSection from '@app/modules/Admin/Voucher/VoucherCreate/BasicInformationSection'
import CodeSection from '@app/modules/Admin/Voucher/VoucherCreate/CodeSection'
import POSSection from '@app/modules/Admin/Voucher/VoucherCreate/POSSection'
import TermAndConditionSection from '@app/modules/Admin/Voucher/VoucherCreate/TermAndConditionSection'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { validationSchema } from './Schema'

const VoucherCreate = ({ merchants, pos, voucherType, tags }) => {
  const { t } = useTranslation()
  const [disabled, setDisabled] = useState(false)

  const [merchantId, setMerchantId] = useState('')

  const initialValues = {
    name: '',
    merchants: '',
    categories: [],
    price: '',
    quantity: '',
    info_link: '',
    tags: [],
    pos: [],
    term: '',
    description: '',
    condition: '',
    how_to_use: '',
    images: [],
    merchant_id: 0,
    type: 'standard',
    limit_min: '',
    limit_max: '',
    limit_code_per_user: 0,
    expired_date: toTextFieldDateFormat(new Date()),
  }

  const data = {
    tags,
  }

  Inertia.on('finish', () => setDisabled(false))

  const onSubmit = (data) => {
    setDisabled(true)
    Inertia.post(route('admin.vouchers.store'), { ...data, merchant: merchantId }, {})
  }

  const {
    register,
    trigger,
    control,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2} sx={{ paddingTop: '10px' }}>
        <Grid item xs={12}>
          <Section title={t('VOUCHER.BASIC_INFORMATION')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <BasicInformationSection
                  control={control}
                  data={data}
                  errors={errors}
                  getValues={getValues}
                  merchants={merchants}
                  register={register}
                  setMerchantId={(value) => setMerchantId(value)}
                  setValue={setValue}
                  trigger={trigger}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('VOUCHER.CODES')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <CodeSection
                  control={control}
                  errors={errors}
                  getValues={getValues}
                  register={register}
                  setValue={setValue}
                  voucherType={voucherType}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('VOUCHER.TERM_AND_CONDITION')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <TermAndConditionSection
                  control={control}
                  getValues={getValues}
                  register={register}
                  setValue={setValue}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('VOUCHER.POS')}>
            <Grid container>
              <Grid item lg={9} md={12} sm={12} xs={12}>
                <POSSection
                  control={control}
                  errors={errors}
                  getValues={getValues}
                  merchantId={merchantId}
                  merchants={merchants}
                  pos={pos}
                  register={register}
                  setValue={setValue}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('VOUCHER.IMAGE')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabelField required recommend={t('VOUCHER.SLIDER_RECOMMEND')} title={t('VOUCHER.SLIDER')}>
                      <Controller
                        control={control}
                        name="images"
                        render={({ field }) => (
                          <InputUpload
                            {...field}
                            multiple
                            buttonName={t('VOUCHER.UPLOAD_IMAGE')}
                            clearErrors={clearErrors}
                            handleImageChange={(files) => setValue('images', files)}
                            id="images"
                            name="images"
                            ref={field.ref}
                            setError={setError}
                            title={t('VOUCHER.UPLOAD_IMAGE')}
                            value={getValues('images')}
                          />
                        )}
                      />

                      {errors.images && <ErrorMessage variant="caption">{errors.images?.message}</ErrorMessage>}
                    </LabelField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Button color="primary" disabled={disabled} sx={{ marginRight: 2 }} type="submit" variant="contained">
            {t('BUTTON.SAVE')}
          </Button>
          <Button color="inherit" href={route('admin.vouchers.index')} variant="contained">
            {t('BUTTON.CANCEL')}
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

export default VoucherCreate
